import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Dropdown, Modal, Form, Input, message } from 'antd';
import { UserOutlined, LogoutOutlined, SettingOutlined, ExclamationCircleOutlined, DownOutlined, MenuOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { getCurrentUser, RBACService } from '../utils/rbac';
import { apiService, getUploadsBaseUrl } from '../services/apiService';
import './AdminHeader.css';

const { confirm } = Modal;

const routeTitles: { [key: string]: string } = {
  '/dashboard': 'DASHBOARD',
  '/superadmin': 'SUPER ADMIN',
  '/sales': 'SALES REPORTS',
  '/pos': 'POINT OF SALE',
  '/purchase-orders': 'PURCHASE ORDERS',
  '/inventory': 'INVENTORY',
  '/products': 'PRODUCTS',
  '/users': 'USERS',
  '/locations': 'LOCATIONS',
  '/documents': 'DOCUMENTS',
  '/reports': 'REPORTS',
  '/vendors': 'VENDORS',
  '/vendor-products': 'VENDOR PRODUCTS',
  '/grpo': 'GRPO',
};

interface HeaderProps {
  isMobile?: boolean;
  sidebarCollapsed?: boolean;
  onOpenSidebar?: () => void;
}

function resolveProfileImageUrl(path?: string | null): string | null {
  if (!path) return null;
  const normalized = String(path).replace(/\\/g, '/').trim();
  if (!normalized) return null;
  if (/^https?:\/\//i.test(normalized)) return normalized;
  if (normalized.startsWith('/uploads/')) return `${getUploadsBaseUrl()}${normalized}`;
  const uploadsSplit = normalized.toLowerCase().split('/uploads/');
  if (uploadsSplit.length > 1) {
    const rel = normalized.substring(normalized.toLowerCase().indexOf('/uploads/'));
    return `${getUploadsBaseUrl()}${rel}`;
  }
  return `${getUploadsBaseUrl()}/${normalized.replace(/^\.?\//, '')}`;
}

const Header: React.FC<HeaderProps> = ({ isMobile = false, sidebarCollapsed = false, onOpenSidebar }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentUser, setCurrentUser] = useState(getCurrentUser());
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileUploading, setProfileUploading] = useState(false);
  const [profileImagePath, setProfileImagePath] = useState<string | null>(null);
  const [profileForm] = Form.useForm();
  const [time, setTime] = useState({ time: '', date: '' });

  const syncCurrentUserPatch = (patch: Record<string, unknown>) => {
    const u = getCurrentUser();
    if (!u) return;
    const merged = { ...u, ...patch };
    RBACService.setCurrentUser(merged as any);
    setCurrentUser(merged as any);
  };

  useEffect(() => {
    const updateCurrentUser = () => setCurrentUser(getCurrentUser());
    window.addEventListener('storage', updateCurrentUser);
    return () => window.removeEventListener('storage', updateCurrentUser);
  }, []);

  useEffect(() => {
    setProfileImagePath((currentUser as any)?.profileImagePath || null);
  }, [currentUser]);

  useEffect(() => {
    const format = () => {
      const now = new Date();
      setTime({
        time: now.toLocaleTimeString('en-GB', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }),
        date: now.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }),
      });
    };
    format();
    const id = setInterval(format, 1000);
    return () => clearInterval(id);
  }, []);

  const getPageTitle = (): string => {
    const path = location.pathname;
    if (routeTitles[path]) return routeTitles[path];
    for (const [route, title] of Object.entries(routeTitles)) {
      if (path.startsWith(route)) return title;
    }
    return 'DASHBOARD';
  };

  const getUserInitials = (): string => {
    if (currentUser?.name) {
      const names = currentUser.name.split(' ');
      if (names.length >= 2) return (names[0][0] + names[1][0]).toUpperCase();
      return currentUser.name.substring(0, 2).toUpperCase();
    }
    const role = (currentUser?.roleName || localStorage.getItem('userRole') || 'USER').toString();
    const parts = role.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return role.substring(0, 2).toUpperCase();
  };

  const handleLogout = () => {
    confirm({
      title: 'Are you sure you want to logout?',
      icon: <ExclamationCircleOutlined />,
      content: 'You will be redirected to the login page.',
      okText: 'Yes, Logout',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('userEmail');
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        localStorage.removeItem('userRole');
        localStorage.removeItem('userRoleId');
        localStorage.removeItem('userId');
        localStorage.removeItem('userName');
        navigate('/user/login');
      },
    });
  };

  const openProfileModal = async () => {
    setProfileModalOpen(true);
    setProfileLoading(true);
    try {
      const me = await apiService.get<any>('/auth/me');
      profileForm.setFieldsValue({
        first_name: me?.first_name || '',
        last_name: me?.last_name || '',
      });
      setProfileImagePath(me?.profile_image_path || (currentUser as any)?.profileImagePath || null);
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Failed to load profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleProfileSave = async () => {
    try {
      const values = await profileForm.validateFields();
      setProfileSaving(true);
      const updated = await apiService.put<any>('/auth/me', {
        first_name: values.first_name?.trim() || '',
        last_name: values.last_name?.trim() || '',
      });
      const fullName = [updated?.first_name, updated?.last_name].filter(Boolean).join(' ').trim() || currentUser?.name || 'User';
      syncCurrentUserPatch({
        name: fullName,
      });
      localStorage.setItem('userName', fullName);
      message.success('Profile updated successfully');
      setProfileModalOpen(false);
    } catch (err: any) {
      if (!err?.errorFields) {
        message.error(err?.response?.data?.detail || 'Failed to update profile');
      }
    } finally {
      setProfileSaving(false);
    }
  };

  const handleProfileImageSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowed.includes(file.type)) {
      message.error('Please select a PNG, JPG, or WEBP image');
      e.target.value = '';
      return;
    }
    const formData = new FormData();
    formData.append('photo_file', file);
    setProfileUploading(true);
    try {
      const res = await apiService.upload<{ profile_image_path?: string }>('/auth/me/profile-photo', formData);
      const nextPath = res?.profile_image_path || null;
      setProfileImagePath(nextPath);
      syncCurrentUserPatch({ profileImagePath: nextPath });
      message.success('Profile photo uploaded');
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Failed to upload profile photo');
    } finally {
      e.target.value = '';
      setProfileUploading(false);
    }
  };

  const userMenuItems: MenuProps['items'] = [
    { key: 'profile', icon: <UserOutlined />, label: 'Profile' },
    { key: 'settings', icon: <SettingOutlined />, label: 'Settings' },
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: 'Logout', danger: true },
  ];

  const handleUserMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'logout') handleLogout();
    else if (key === 'profile') openProfileModal();
    else if (key === 'settings') openProfileModal();
  };

  /**
   * Company User Portal: show assigned store/warehouse whenever the user has a location
   * (including Enterprise Manager, General Manager, Manager, etc.).
   */
  const hasAssignedLocation = Boolean(
    (currentUser?.locationName && String(currentUser.locationName).trim()) ||
      (currentUser?.locationId != null && String(currentUser.locationId).trim() !== '')
  );
  const showStorePill = Boolean(currentUser) && hasAssignedLocation;
  const storeLabel = currentUser?.locationName && String(currentUser.locationName).trim()
    ? String(currentUser.locationName).trim()
    : currentUser?.locationId != null && String(currentUser.locationId).trim() !== ''
      ? `Location ${currentUser.locationId}`
      : '';

  return (
    <>
      <div className="admin-header">
        <div className="admin-header-left">
        {isMobile && (
          <button
            type="button"
            className="admin-header-menu-btn"
            onClick={onOpenSidebar}
            aria-label="Open navigation menu"
          >
            <MenuOutlined />
          </button>
        )}
        <div className="admin-header-breadcrumb-wrap">
          <span className="admin-header-breadcrumb-text">Home</span>
          <span className="admin-header-breadcrumb-sep"> / </span>
          <span className="admin-header-breadcrumb-current">{getPageTitle()}</span>
        </div>
      </div>
        <div className="admin-header-right">
        {showStorePill ? (
          <div
            className="admin-header-location-pill"
            title={storeLabel}
            aria-label={`Active store or warehouse: ${storeLabel}`}
          >
            <span className="admin-header-location-dot" aria-hidden />
            <span className="admin-header-location-text">{storeLabel}</span>
          </div>
        ) : null}
        <div className="admin-header-datetime">
          <div className="admin-header-time">{time.time}</div>
          <div className="admin-header-date">{time.date}</div>
        </div>
        <Dropdown
          menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
          placement="bottomRight"
          arrow
        >
          <button type="button" className="admin-header-user-pill">
            {resolveProfileImageUrl(profileImagePath) ? (
              <img
                src={resolveProfileImageUrl(profileImagePath) || ''}
                alt="Profile"
                className="admin-avatar-image admin-avatar-image--pill"
              />
            ) : (
              <div className="admin-avatar-icon admin-avatar-icon--pill">{getUserInitials()}</div>
            )}
            <span className="admin-header-user-name">{(currentUser?.name || 'User').trim()}</span>
            <DownOutlined className="admin-header-chevron" />
          </button>
        </Dropdown>
        </div>
      </div>
      <Modal
        title="Profile Settings"
        open={profileModalOpen}
        onCancel={() => setProfileModalOpen(false)}
        onOk={handleProfileSave}
        okText={profileSaving ? 'Saving...' : 'Save'}
        okButtonProps={{ loading: profileSaving }}
        confirmLoading={profileSaving}
      >
        <div className="admin-profile-editor">
          <div className="admin-profile-photo-section">
            {resolveProfileImageUrl(profileImagePath) ? (
              <img src={resolveProfileImageUrl(profileImagePath) || ''} alt="Profile" className="admin-profile-photo-preview" />
            ) : (
              <div className="admin-avatar-icon admin-profile-photo-fallback">{getUserInitials()}</div>
            )}
            <label className="admin-profile-upload-btn">
              {profileUploading ? 'Uploading...' : 'Upload photo'}
              <input
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                onChange={handleProfileImageSelected}
                disabled={profileUploading || profileLoading}
                style={{ display: 'none' }}
              />
            </label>
          </div>
          <Form form={profileForm} layout="vertical" disabled={profileLoading || profileSaving}>
            <Form.Item
              label="First name"
              name="first_name"
              rules={[{ required: true, message: 'First name is required' }]}
            >
              <Input placeholder="First name" />
            </Form.Item>
            <Form.Item
              label="Last name"
              name="last_name"
              rules={[{ required: true, message: 'Last name is required' }]}
            >
              <Input placeholder="Last name" />
            </Form.Item>
          </Form>
        </div>
      </Modal>
    </>
  );
};

export default Header;
